import { Link } from 'react-router-dom';

/**
 * Privacy Policy — public, no login required. Linked from the Twilio SMS
 * opt-in card and referenced during registration. Keep this current;
 * Twilio toll-free / A2P reviewers may inspect it.
 */
export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white text-gray-800">
      <header className="border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="text-navy font-bold text-lg">Park City Chiefs</Link>
          <Link to="/" className="text-sm text-navy hover:underline" data-testid="privacy-back-home">Back to site</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="privacy-page">
        <h1 className="text-3xl font-bold text-navy">Privacy Policy</h1>
        <p className="text-sm text-gray-500">Last updated: February 2026</p>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">1. Information we collect</h2>
          <p>
            Park City Chiefs collects the information you provide when you register a player,
            create an account, or opt in to communications. This includes:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Parent and guardian names, email addresses, mailing addresses, and phone numbers.</li>
            <li>Player names, dates of birth, medical/emergency contacts, and division/team assignments.</li>
            <li>Payment records (handled by our processor; we do not store full card numbers on our servers).</li>
            <li>Volunteer availability and role signups.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">2. How we use your information</h2>
          <p>We use your information only to run the organization:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Manage rosters, registrations, and payments.</li>
            <li>Send you the specific communications you have consented to (in-app, email, SMS).</li>
            <li>Coordinate volunteers, coaches, and event attendance.</li>
            <li>Maintain safety and eligibility records.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">3. SMS text messaging</h2>
          <p>
            If you opt in to SMS alerts, we store your consent status, the timestamp of your
            consent, and the source (self-serve toggle, admin update, or reply START). We send
            your mobile number to our SMS delivery provider only to deliver the messages you
            have consented to receive. We do not sell, rent, or share your mobile number or SMS
            content with third parties for their marketing purposes.
          </p>
          <p>
            You can opt out at any time by replying <strong>STOP</strong> to any message, or by
            unchecking the SMS alerts box on your account settings. Opting out stops future SMS
            immediately; messages already queued may still be delivered.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">4. Who we share information with</h2>
          <p>We share information only with service providers who help us run the organization:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Payment processor (for registration and store purchases).</li>
            <li>Email delivery provider (for transactional email you have consented to).</li>
            <li>SMS delivery provider (only if you have opted in to SMS).</li>
            <li>League and division authorities (rosters, insurance, and eligibility records only where required).</li>
          </ul>
          <p>We do not sell your personal information.</p>
          <p
            className="font-semibold text-navy border-l-4 border-navy pl-4 bg-gray-50 py-3"
            data-testid="privacy-sms-exclusion-clause"
          >
            All the above categories exclude text messaging originator opt-in data and consent;
            this information won&rsquo;t be shared with any third parties.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">5. Children&apos;s data</h2>
          <p>
            Our players are typically minors. Parents and legal guardians provide and control
            the information on file for their players. Contact us to review, correct, or delete
            your family&apos;s records.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">6. Security</h2>
          <p>
            We use industry-standard encryption in transit, access controls, and role-based
            permissions to protect your data. No system is perfectly secure, but we work
            continuously to keep your information safe.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">7. Your rights</h2>
          <p>You can:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>View and update your family&apos;s information from your account.</li>
            <li>Opt out of SMS at any time.</li>
            <li>Request deletion of your household account (contact us).</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">8. Contact</h2>
          <p>
            Email <a href="mailto:info@parkcitychiefs.org" className="text-navy underline">info@parkcitychiefs.org</a> with
            any privacy questions.
          </p>
        </section>
      </main>
    </div>
  );
}
